/**
 * ChatThread - Message trail with auto-scroll and date grouping
 * TODO(Mudo): virtualize long threads (react-virtual) if needed
 */

import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import { useMessages } from './MessagesContext';
import { isImageAvatarUrl } from '../../lib/avatars';

export default function ChatThread({ onBack }) {
  const { 
    activeThreadId, 
    messagesByThread, 
    threads, 
    userId, 
    sendMessage, 
    sendingMessage,
    userProfiles,
    namesByUserId,
    deleteThread,
  } = useMessages();
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const thread = threads.find((t) => t.id === activeThreadId);
  // Dedupe messages by id to avoid duplicate React keys when realtime + optimistic collide
  const rawMessages = messagesByThread[activeThreadId] || [];
  const seen = new Set();
  const messages = rawMessages.filter((m) => {
    const id = m.id || `${m.createdAt || m.created_at}-${m.senderId || m.sender_id}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  const otherUserId = thread?.participants.find((id) => id !== userId);
  const otherUser = userProfiles[otherUserId] || {};
  const otherUserName = namesByUserId?.[otherUserId] || otherUser.display_name || 'User';
  const ownName = namesByUserId?.[userId] || 'You';
  const otherUserAvatar = otherUser.avatar_url || otherUser.avatar_emoji || '👤';

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.createdAt || message.created_at).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  const handleSend = async (body) => {
    await sendMessage(activeThreadId, body);
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setDeleting(true);
    try {
      const result = await deleteThread(activeThreadId);
      
      if (result.success) {
        setShowDeleteConfirm(false);
        // Thread and messages already removed from state by deleteThread
      } else {
        throw result.error || new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting thread:', error);
      alert('Failed to delete chat. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!thread) {
    return (
      <div className="col-span-8 md:col-span-8 lg:col-span-9 xl:col-span-9 flex flex-col items-center justify-center bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">💬</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Select a conversation</h3>
          <p className="text-white/60">Choose a message from the left to start chatting</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="col-span-8 md:col-span-8 lg:col-span-9 xl:col-span-9 flex flex-col bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center gap-3 bg-black/10">
        {onBack && (
          <button
            onClick={onBack}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        
        <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xl overflow-hidden flex-shrink-0">
          {isImageAvatarUrl(otherUserAvatar) ? (
            <img src={otherUserAvatar} alt={otherUserName} className="w-full h-full object-cover" />
          ) : (
            <span>{otherUserAvatar}</span>
          )}
        </div>
        
        <div className="flex-1">
          <h3 className="font-semibold text-white">{otherUserName}</h3>
          <p className="text-xs text-white/60">Active now</p>
        </div>

        {/* Delete Button */}
        <div className="flex items-center gap-2">
          {showDeleteConfirm ? (
            <>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-3 py-1.5 text-xs rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-1.5 text-xs rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 rounded-lg hover:bg-red-600/20 text-white/70 hover:text-red-400 transition-colors"
              title="Delete chat"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
        style={{ scrollBehavior: 'smooth' }}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-4">
              <span className="text-3xl">👋</span>
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">Start the conversation</h4>
            <p className="text-sm text-white/60">
              Send a message to begin chatting with {otherUserName}
            </p>
          </div>
        ) : (
          <>
            {Object.entries(groupedMessages).map(([date, msgs]) => (
              <div key={date}>
                {/* Date Separator */}
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-xs text-white/40 px-2">{date}</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Messages */}
                {msgs.map((message, idx) => {
                  const isOwn = message.senderId === userId || message.sender_id === userId;
                  const senderName = isOwn ? ownName : otherUserName;
                  return (
                  <MessageBubble
                    key={`${message.id}-${message.created_at || message.createdAt || idx}`}
                    message={message}
                    isOwn={isOwn}
                    avatar={otherUserAvatar}
                    senderName={senderName}
                  />
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <MessageInput 
        onSend={handleSend} 
        disabled={sendingMessage}
        placeholder={`Message ${otherUserName}…`}
      />
    </div>
  );
}
